import { Autocomplete, Chip, TextField } from "@mui/material";
import * as React from "react";

import { useSearchOrgs } from "@/shared/domain/orgs/hooks/useSearchOrgs";

interface OrgAutocompleteProps {
  selectedOrgIds: string[];
  onChange: (orgIds: string[]) => void;
  helperText?: string;
}

/**
 * Server-side org search autocomplete with chips.
 * Searches by org name or ID (minimum 2 characters).
 */
export function OrgAutocomplete({
  selectedOrgIds,
  onChange,
  helperText = "Leave empty to make the server available to all organizations",
}: OrgAutocompleteProps) {
  const [inputValue, setInputValue] = React.useState("");
  const [debouncedInput, setDebouncedInput] = React.useState("");

  React.useEffect(() => {
    const timer = setTimeout(() => setDebouncedInput(inputValue), 300);
    return () => clearTimeout(timer);
  }, [inputValue]);

  const { data: searchResults, isLoading } = useSearchOrgs(debouncedInput);

  // Build option list: search results + any already-selected IDs not in results
  const options = React.useMemo(() => {
    const resultIds = new Set((searchResults ?? []).map((o) => o.org_id));
    const extraSelected = selectedOrgIds
      .filter((id) => !resultIds.has(id))
      .map((id) => ({ org_id: id, name: id }));
    return [...(searchResults ?? []), ...extraSelected];
  }, [searchResults, selectedOrgIds]);

  return (
    <Autocomplete
      multiple
      freeSolo
      options={options}
      getOptionLabel={(option) => {
        if (typeof option === "string") {
          return option;
        }
        return option.name
          ? `${option.name} (${option.org_id})`
          : option.org_id;
      }}
      isOptionEqualToValue={(option, value) => {
        const optId = typeof option === "string" ? option : option.org_id;
        const valId = typeof value === "string" ? value : value.org_id;
        return optId === valId;
      }}
      value={selectedOrgIds.map(
        (id) =>
          options.find((o) => o.org_id === id) ?? { org_id: id, name: id },
      )}
      onChange={(_event, newValue) => {
        const ids = newValue.map((v) => (typeof v === "string" ? v : v.org_id));
        onChange(ids);
      }}
      inputValue={inputValue}
      onInputChange={(_event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      loading={isLoading}
      filterOptions={(x) => x}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => {
          const label = typeof option === "string" ? option : option.org_id;
          const { key, ...tagProps } = getTagProps({ index });
          return <Chip key={key} label={label} size="small" {...tagProps} />;
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label="Organizations"
          placeholder={
            selectedOrgIds.length === 0
              ? "Search by name or ID (global if empty)"
              : "Add more..."
          }
          margin="dense"
          size="small"
          helperText={helperText}
        />
      )}
    />
  );
}
